/* eslint-disable max-lines */
import { formatShortDateWithWeekday } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { ThemeProvider } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { convertToGeoLocation } from "@src/appV2/Location";
import { List, ListItem, useModalState } from "@src/appV2/redesign/system/ui-components";
import { getTheme } from "@src/appV2/redesign/system/ui-theme";
import { DEFAULT_TIMEZONE } from "@src/appV2/Shifts/Shift/constants";
import {
  ExtraWorkedTimeType,
  type ShiftPeriod,
  shiftPeriodToTimeSlot,
} from "@src/appV2/Shifts/Shift/types";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { differenceInMinutes, parseISO, subMinutes } from "date-fns";
import { useRef } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { InfoCard } from "../../components/InfoCard";
import { PageWrapper } from "../../components/PageWrapper";
import { ShiftDurationListItem } from "../../Shift/DurationListItem";
import { ShiftHighlightsAndMapListItem } from "../../Shift/HighlightsAndMapListItem";
import { ShiftWorkplaceListItem } from "../../Shift/WorkplaceListItem";
import { SHIFT_DISCOVERY_WORKPLACE_PROFILE_FULL_PATH } from "../../ShiftDiscovery/paths";
import { WorkingThisShiftListItem } from "../../WorkWithFriends/components/WorkingThisShiftListItem";
import { WorkWithFriendsProvider } from "../../WorkWithFriends/WorkWithFriendsContext";
import { PageContent } from "../components/PageContent";
import { ReturnForSignatureBottomSheet } from "../components/ReturnForSignatureBottomSheet";
import { UploadTimesheetBottomSheet } from "../UnverifiedShifts/UploadTimesheet/UploadTimesheetBottomSheet";
import { type PastShiftDetailsData } from "./api/useGetPastShiftDetailsData";
import { useGetWorkedWithProfessionals } from "./api/useGetWorkedWithProfessionals";
import { useGetWorkerPaymentsByShift } from "./api/useGetWorkerPaymentsByShift";
import { PastShiftDetailsFooter } from "./components/PastShiftDetailsFooter";
import { PastShiftDetailsHeader } from "./components/PastShiftDetailsHeader";
import { PaymentHistorySection } from "./components/PaymentHistorySection";
import { TimesheetListItem, type TimesheetTimestamps } from "./components/TimesheetListItem";
import { usePastShiftDetailsActions } from "./hooks/usePastShiftDetailsActions";

interface PastShiftDetailsPageProps {
  data: PastShiftDetailsData;
  refetchExtraWorkedTimeRequests: () => void;
}

export function PastShiftDetailsPage(props: PastShiftDetailsPageProps) {
  const { data, refetchExtraWorkedTimeRequests } = props;
  const {
    shift,
    request,
    missedPunchRequestStatus,
    isMissedPunchRequestEnabled,
    canSubmitTimesheet,
    totalPayInDollars,
    hourlyRateInDollars,
    requestedAmountInDollars,
  } = data;

  const isFullShiftPayment = request?.attributes.isFullShiftPayment ?? false;
  const statusUpdateReason = request?.attributes.statusUpdateReason ?? undefined;
  const { userId } = useDefinedWorker();
  const history = useHistory();
  const shiftId = shift._id;
  const facilityTimeZone = shift.facility.tmz ?? DEFAULT_TIMEZONE;
  const workplaceLocation = shift.facility.geoLocation.coordinates
    ? convertToGeoLocation(shift.facility.geoLocation.coordinates)
    : undefined;
  const isTimesheetUploaded = (shift.timecard?.files?.length ?? 0) > 0;
  const isVerified = shift.verified ?? false;
  const requiresBreak = shift.facility.requiresLunchBreak ?? false;

  const shouldFetchPaymentHistory =
    isVerified || missedPunchRequestStatus === "pending" || missedPunchRequestStatus === "declined";

  const { data: payments } = useGetWorkerPaymentsByShift(shiftId, {
    enabled: shouldFetchPaymentHistory,
  });

  const verifiedTimesheet: TimesheetTimestamps = {
    clockIn: shift.clockInOut?.start ?? undefined,
    clockOut: shift.clockInOut?.end ?? undefined,
    breakStart: shift.lunchInOut?.start ?? undefined,
    breakEnd: shift.lunchInOut?.end ?? undefined,
  };
  const proposedTimesheet = getProposedTimesheet(request, verifiedTimesheet);

  const workplaceId = shift.facility.userId ?? shift.facilityId;
  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId,
  });
  const { filteredSlot, friendsMap, outboundFriendRequestsSet } = useGetWorkedWithProfessionals({
    shiftId,
    shiftStart: shift.start,
    shiftEnd: shift.end,
    workplaceId,
    workerId: userId,
  });

  useLogEffect(APP_V2_USER_EVENTS.PAST_SHIFT_DETAILS_PAGE_VIEWED, { shiftId });

  const dateLabel = formatShortDateWithWeekday(shift.start, { timeZone: facilityTimeZone });
  const shiftTimeSlot = shiftPeriodToTimeSlot[shift.name as ShiftPeriod];
  const shiftDurationInHours = differenceInMinutes(parseISO(shift.end), parseISO(shift.start)) / 60;
  const showDeclineReason =
    missedPunchRequestStatus === "declined" && isDefined(statusUpdateReason);

  const returnForSignatureModalState = useModalState();
  const uploadTimesheetModalState = useModalState();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const {
    onSubmitNewRequest,
    onAcceptOriginalTimes,
    onContactSupport,
    onRequestPayAdjustment,
    handleRecheckLocation,
    navigateToSubmitTimesheet,
    isCheckingGeofence,
    isAcceptingOriginalTimes,
  } = usePastShiftDetailsActions({
    shiftId,
    shiftEnd: shift.end,
    workerId: userId,
    workplaceId,
    workplaceLocation,
    requestId: request?.id,
    onWorkerOutsideGeofence: returnForSignatureModalState.openModal,
    onAcceptOriginalTimesSuccess: refetchExtraWorkedTimeRequests,
  });

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <PastShiftDetailsHeader
          shiftId={shiftId}
          dateLabel={dateLabel}
          qualificationName={shift.agentReq}
          missedPunchRequestStatus={missedPunchRequestStatus}
          isVerified={isVerified}
          totalPayInDollars={totalPayInDollars}
          hourlyRateInDollars={hourlyRateInDollars}
          requestedAmountInDollars={requestedAmountInDollars}
          isFullShiftPayment={isFullShiftPayment}
          scrollContainerRef={scrollContainerRef}
        />
        <PageContent ref={scrollContainerRef} sx={{ paddingX: 7, paddingY: 1 }}>
          <List>
            {showDeclineReason && (
              <ListItem>
                <InfoCard
                  iconType="text-bubble-filled"
                  title="Reason for declining"
                  description={statusUpdateReason}
                />
              </ListItem>
            )}
            <ShiftDurationListItem
              shiftTimeSlot={shiftTimeSlot}
              shiftStartIso={shift.start}
              shiftEndIso={shift.end}
              shiftDurationInHours={shiftDurationInHours}
              breakDurationInMinutes={mandatoryBreakDurationInMinutes}
              requiresBreak={requiresBreak}
              workplaceTimeZone={facilityTimeZone}
              displayVariant="overview"
            />
            <TimesheetListItem
              verifiedTimesheet={verifiedTimesheet}
              proposedTimesheet={proposedTimesheet}
              isVerified={isVerified}
              verifiedTimeInHours={isVerified && isDefined(shift.time) ? shift.time : undefined}
              isFullShiftPayment={isFullShiftPayment}
              pendingApprovalInMinutes={request?.attributes.timeRequestedInMinutes ?? undefined}
              missedPunchRequestStatus={missedPunchRequestStatus}
              isMissedPunchRequestEnabled={isMissedPunchRequestEnabled}
              canSubmitTimesheet={canSubmitTimesheet}
              facilityTimeZone={facilityTimeZone}
              isCheckingGeofence={isCheckingGeofence}
              onRequestPayAdjustment={onRequestPayAdjustment}
            />
            {isDefined(payments) && payments.length > 0 && (
              <PaymentHistorySection payments={payments} />
            )}
            {isDefined(workplaceLocation) && (
              <>
                <ShiftWorkplaceListItem
                  hideDistanceIcon
                  ratingFormat="compact"
                  workplaceId={workplaceId}
                  workplaceName={shift.facility.name}
                  workplaceType={shift.facility.type ?? ""}
                  workplaceLocation={workplaceLocation}
                  onClick={() => {
                    logEvent(APP_V2_USER_EVENTS.PAST_SHIFT_DETAILS_WORKPLACE_CARD_TAPPED, {
                      shiftId,
                      workplaceId,
                    });

                    history.push(
                      generatePath(SHIFT_DISCOVERY_WORKPLACE_PROFILE_FULL_PATH, {
                        workplaceId,
                      })
                    );
                  }}
                />
                <ShiftHighlightsAndMapListItem
                  isMapEnabled
                  hasRoundedMapCorners
                  workplaceId={workplaceId}
                  workplaceName={shift.facility.name}
                  workplaceLocation={workplaceLocation}
                  workplaceFormattedAddress={shift.facility.fullAddress?.formatted}
                  isDividerEnabled={isDefined(filteredSlot)}
                />
              </>
            )}
            {isDefined(filteredSlot) && (
              <WorkWithFriendsProvider>
                <WorkingThisShiftListItem
                  nameSemibold
                  title="Professionals you worked with"
                  showAvailabilityText={false}
                  icon="users-filled"
                  shiftSlots={filteredSlot}
                  shiftId={shiftId}
                  friendsMap={friendsMap}
                  outboundFriendRequestsSet={outboundFriendRequestsSet}
                  isBookable={false}
                />
              </WorkWithFriendsProvider>
            )}
          </List>
        </PageContent>
        <PastShiftDetailsFooter
          missedPunchRequestStatus={missedPunchRequestStatus}
          isVerified={isVerified}
          isFullShiftPayment={isFullShiftPayment}
          requestExpiryTime={request?.meta?.computedExpirationTime ?? undefined}
          isMissedPunchRequestEnabled={isMissedPunchRequestEnabled}
          isTimesheetUploaded={isTimesheetUploaded}
          canSubmitTimesheet={canSubmitTimesheet}
          facilityTimeZone={facilityTimeZone}
          isAcceptingOriginalTimes={isAcceptingOriginalTimes}
          isCheckingGeofence={isCheckingGeofence}
          actions={{
            onSubmitNewRequest,
            onAcceptOriginalTimes,
            onUploadTimesheet: () => {
              logEvent(APP_V2_USER_EVENTS.PAST_SHIFT_DETAILS_ACTION_TAPPED, {
                shiftId,
                action: "submit_timesheet",
              });
              uploadTimesheetModalState.openModal();
            },
            onContactSupport,
          }}
        />
      </PageWrapper>

      <UploadTimesheetBottomSheet
        modalState={uploadTimesheetModalState}
        shiftId={shiftId}
        isTimesheetUploaded={isTimesheetUploaded}
        onUploadStarted={() => {
          logEvent(APP_V2_USER_EVENTS.PAST_SHIFT_DETAILS_UPLOAD_TIMESHEET_STARTED, {
            shiftId,
            workerId: userId,
          });
        }}
        onSuccess={({ uploadDurationInMs, updateTimecardDurationInMs }) => {
          logEvent(APP_V2_USER_EVENTS.PAST_SHIFT_DETAILS_UPLOAD_TIMESHEET_UPLOADED, {
            shiftId,
            workerId: userId,
            isReupload: isTimesheetUploaded,
            uploadDurationInMs,
            updateTimecardDurationInMs,
          });
        }}
        onError={(error) => {
          logEvent(APP_V2_USER_EVENTS.PAST_SHIFT_DETAILS_UPLOAD_TIMESHEET_UPLOAD_ERROR, {
            shiftId,
            workerId: userId,
            error: error?.message ?? "Unknown error",
          });
        }}
      />

      <ReturnForSignatureBottomSheet
        modalState={returnForSignatureModalState}
        shiftId={shiftId}
        workerId={userId}
        isCheckingLocation={isCheckingGeofence}
        isShiftVerified={isVerified}
        onRecheckLocation={async () => {
          const outcome = await handleRecheckLocation();
          if (outcome === "inside") {
            returnForSignatureModalState.closeModal();
          }

          return outcome;
        }}
        onCancel={() => {
          returnForSignatureModalState.closeModal();
        }}
        onConfirm={() => {
          navigateToSubmitTimesheet(true);
        }}
      />
    </ThemeProvider>
  );
}

function getProposedTimesheet(
  request: PastShiftDetailsData["request"],
  verifiedTimesheet: TimesheetTimestamps
): TimesheetTimestamps | undefined {
  if (!isDefined(request)) {
    return undefined;
  }

  if (request.attributes.type === ExtraWorkedTimeType.BREAK) {
    return {
      clockIn: verifiedTimesheet.clockIn,
      clockOut: verifiedTimesheet.clockOut,
      breakStart: verifiedTimesheet.breakStart,
      breakEnd:
        isDefined(verifiedTimesheet.breakEnd) &&
        isDefined(request.attributes.timeRequestedInMinutes)
          ? subMinutes(
              parseISO(verifiedTimesheet.breakEnd),
              request.attributes.timeRequestedInMinutes
            ).toISOString()
          : verifiedTimesheet.breakEnd,
    };
  }

  return {
    clockIn: request.attributes.start ?? undefined,
    clockOut: request.attributes.end ?? undefined,
    breakStart: request.attributes.breakStartEnd?.start ?? undefined,
    breakEnd: request.attributes.breakStartEnd?.end ?? undefined,
  };
}
/* eslint-enable max-lines */
